import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { API_BASE, extractErrorMessage } from "@/lib/api";
import { useLang } from "@/i18n";
import {
  Upload,
  FileJson,
  FileText,
  Download,
  Eye,
  CheckCircle2,
  AlertTriangle,
  ShieldAlert,
  ArrowRight,
  Trash2,
  Edit3,
} from "lucide-react";
import { toast } from "sonner";

const SENSITIVE = ["health", "injury", "psychological", "wellbeing", "minor"];

const tabs = [
  { id: "json_paste", icon: FileJson, key: "tabJsonPaste" },
  { id: "json_file", icon: Upload, key: "tabJsonFile" },
  { id: "docx_file", icon: FileText, key: "tabDocxFile" },
];

const AdminImport = () => {
  const { t, lang, pickI18n, tProfile } = useLang();
  const [profiles, setProfiles] = useState([]);
  const [activeTab, setActiveTab] = useState("json_paste");
  const [rawJson, setRawJson] = useState("");
  const [items, setItems] = useState([]);            // parsed candidates
  const [qaReports, setQaReports] = useState([]);    // Phase A10-C — 15-lang QA summary
  const [commitReports, setCommitReports] = useState([]); // Phase A10-C — after commit
  const [warning, setWarning] = useState("");
  const [source, setSource] = useState("");
  const [parsing, setParsing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [history, setHistory] = useState([]);
  const fileRef = useRef(null);

  const labelFor = (o, base, k) => o[`${base}_${k}`] || o[`${base}_en`];

  useEffect(() => {
    api.get("/profiles").then((r) => setProfiles(r.data));
    api.get("/admin/import/events").then((r) => setHistory(r.data)).catch(() => {});
  }, []);

  const refreshHistory = () => api.get("/admin/import/events").then((r) => setHistory(r.data));

  const downloadTemplate = async () => {
    const { data } = await api.get("/admin/import/template");
    const blob = new Blob([JSON.stringify({ questionnaires: [data] }, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "teamsportworld_questionnaire_template.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  // Assign a stable client-side _uid to each parsed item so React keys remain
  // stable across re-renders even when items are removed/edited from the batch.
  const withStableIds = (rawItems) => (rawItems || []).map((it) => ({
    _uid: crypto.randomUUID(),
    ...it,
  }));

  const previewFromPaste = async () => {
    if (!rawJson.trim()) { toast.error(lang === "es" ? "Pega un JSON válido" : lang === "zh" ? "请粘贴有效 JSON" : "Paste valid JSON"); return; }
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("raw_json", rawJson);
      const { data } = await api.post("/admin/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setItems(withStableIds(data.items));
      setQaReports(data.qa_reports || []);
      setCommitReports([]);
      setWarning(data.warning || "");
      setSource(data.source || "paste");
      toast.success(lang === "es" ? `Se detectaron ${data.items.length} cuestionarios` : lang === "zh" ? `检测到 ${data.items.length} 份问卷` : `Detected ${data.items.length} questionnaires`);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Parse error"));
    } finally { setParsing(false); }
  };

  const previewFromFile = async (file) => {
    if (!file) return;
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setItems(withStableIds(data.items));
      setQaReports(data.qa_reports || []);
      setCommitReports([]);
      setWarning(data.warning || "");
      setSource(data.source || file.name);
      toast.success(lang === "es" ? `Se detectaron ${data.items.length} cuestionarios` : lang === "zh" ? `检测到 ${data.items.length} 份问卷` : `Detected ${data.items.length} questionnaires`);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Parse error"));
    } finally { setParsing(false); }
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (f) previewFromFile(f);
  };

  const updateItemField = (idx, patch) => setItems((arr) => arr.map((it, i) => i === idx ? { ...it, ...patch } : it));

  const removeItem = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));

  const commit = async () => {
    if (items.length === 0) return;
    if (batchHasMissingOptions) {
      toast.error(t("admin.builderMissingOptions").replace("{n}", "?"));
      return;
    }
    setCommitting(true);
    try {
      const { data } = await api.post("/admin/import/commit", items);
      toast.success(lang === "es" ? `Importados: ${data.imported}` : lang === "zh" ? `已导入: ${data.imported}` : `Imported: ${data.imported}`);
      setCommitReports(data.qa_reports || []);
      setItems([]);
      setQaReports([]);
      setWarning("");
      setRawJson("");
      setSource("");
      if (fileRef.current) fileRef.current.value = "";
      await refreshHistory();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Import error"));
    } finally { setCommitting(false); }
  };

  const titleStr = (it) => pickI18n(it, "title") || it.title_en || "(untitled)";
  const sensitiveCount = (it) => (it.questions || []).filter((q) => (q.sensitive_tags || []).length > 0).length;
  // Builder safety guard mirrored in the Import Center: choice-type questions
  // that arrive without options would render as a warning on the public form.
  // Surface this at admin-review time so the source can be fixed before the
  // questionnaire is committed.
  const CHOICE_TYPES = new Set(["single_choice", "multi_choice", "multiple_choice", "checkbox", "radio"]);
  const missingOptionsIdxs = (it) => (it.questions || [])
    .map((q, i) => CHOICE_TYPES.has((q?.type || "").toLowerCase()) && !(q.options || []).length ? i + 1 : null)
    .filter((v) => v !== null);
  const hasMissingOptions = (it) => missingOptionsIdxs(it).length > 0;
  const batchHasMissingOptions = items.some(hasMissingOptions);

  return (
    <div className="space-y-6 max-w-5xl" data-testid="admin-import">
      <div>
        <div className="tsw-overline">{t("admin.title")}</div>
        <h1 className="font-display font-bold text-3xl text-slate-900 flex items-center gap-2">
          <Upload className="w-7 h-7 text-emerald-600" />
          {t("admin.import_importCenter")}
        </h1>
        <p className="text-slate-600 mt-2 text-sm max-w-3xl">
          {t("admin.import_importIntro")}
        </p>
      </div>

      <div className="rounded-lg border border-emerald-100 bg-emerald-50 p-4 text-sm text-emerald-900 flex items-start gap-2" data-testid="import-policy">
        <ShieldAlert className="w-5 h-5 shrink-0 mt-0.5" />
        <div>
          <strong>{t("admin.import_importRulesTitle")}:</strong>{" "}
          {t("admin.import_importRulesBody").replace("{tags}", "health, injury, psychological, wellbeing, minor")}
        </div>
      </div>

      {/* Phase A10-C — Always-inactive banner */}
      <div className="rounded-lg border border-sky-100 bg-sky-50 p-4 text-sm text-sky-900 flex items-start gap-2" data-testid="import-15lang-banner">
        <ShieldAlert className="w-5 h-5 shrink-0 mt-0.5" />
        <div>
          <strong>{t("admin.import_alwaysInactiveTitle")}:</strong>{" "}
          {t("admin.import_alwaysInactiveBody")}
        </div>
      </div>

      {/* Phase A10-C — Post-commit QA summary panel */}
      {commitReports.length > 0 && (
        <div className="space-y-3" data-testid="import-commit-qa-panel">
          <div className="font-display font-semibold text-slate-900 text-base">
            {t("admin.import_qaSummaryTitle")}
          </div>
          {commitReports.map((r, i) => (
            <div key={r.questionnaire_id || i} className="tsw-card p-4 space-y-2" data-testid={`import-qa-${i}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="font-semibold text-slate-900">{r.title_en || "(untitled)"}</div>
                <span className="tsw-chip bg-slate-100 text-slate-600">{r.is_active ? t("admin.statusActive") : t("admin.statusInactive")}</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs text-slate-700">
                <div><div className="text-slate-500">{t("admin.import_qaTotalSections")}</div><div className="font-medium">{r.totals?.sections ?? 0}</div></div>
                <div><div className="text-slate-500">{t("admin.import_qaTotalQuestions")}</div><div className="font-medium">{r.totals?.questions ?? 0}</div></div>
                <div><div className="text-slate-500">{t("admin.import_qaTotalOptions")}</div><div className="font-medium">{r.totals?.options ?? 0}</div></div>
                <div><div className="text-slate-500">{t("admin.import_qaTranslationKeysCreated")}</div><div className="font-medium">{r.translation_keys_created ?? 0}</div></div>
                <div><div className="text-slate-500">{t("admin.import_qaDraftCreated")}</div><div className="font-medium">{r.draft_translations_created ?? 0}</div></div>
                <div><div className="text-slate-500">{t("admin.import_qaReviewedPreserved")}</div><div className="font-medium">{r.reviewed_translations_preserved ?? 0}</div></div>
                <div><div className="text-slate-500">{t("admin.import_qaLanguagesGenerated")}</div><div className="font-medium">{(r.languages_generated || []).length}</div></div>
                <div><div className="text-slate-500">{t("admin.import_qaForcedInactive")}</div><div className="font-medium">{r.forced_inactive ? "Yes" : "No"}</div></div>
              </div>
              {Object.keys(r.missing_translations || {}).length > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-slate-600">{t("admin.import_qaMissingTranslations")}</summary>
                  <ul className="mt-1 grid grid-cols-2 sm:grid-cols-4 gap-1 text-slate-700">
                    {Object.entries(r.missing_translations).map(([code, n]) => (
                      <li key={code}><span className="font-mono text-slate-500">{code}</span>: {n}</li>
                    ))}
                  </ul>
                </details>
              )}
              {(r.warnings || []).length > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-amber-700">{t("admin.import_qaWarnings")} ({(r.warnings || []).length})</summary>
                  <ul className="mt-1 list-disc ms-5 text-amber-900 space-y-0.5">
                    {(r.warnings || []).map((w, wi) => <li key={wi}>{w}</li>)}
                  </ul>
                </details>
              )}
              {(r.would_overwrite_reviewed || []).length > 0 && (
                <div className="rounded border border-rose-300 bg-rose-50 p-2 text-xs text-rose-900">
                  <strong>{t("admin.import_qaWouldOverwriteReviewed")}:</strong> {(r.would_overwrite_reviewed || []).join(", ")}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Phase A10-C — Preview-time QA summary (per item) */}
      {qaReports.length > 0 && items.length > 0 && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3 text-xs text-emerald-900" data-testid="import-preview-qa-panel">
          <div className="font-semibold mb-1">{t("admin.import_qaPreviewTitle")}</div>
          {qaReports.map((r, i) => (
            <div key={i} className="flex flex-wrap gap-x-4 gap-y-1" data-testid={`import-preview-qa-${i}`}>
              <span>{t("admin.import_qaTotalQuestions")}: <strong>{r.totals?.questions ?? 0}</strong></span>
              <span>{t("admin.import_qaTotalOptions")}: <strong>{r.totals?.options ?? 0}</strong></span>
              <span>{t("admin.import_qaTranslationKeysCreated")}: <strong>{r.translation_keys_created ?? 0}</strong></span>
              <span>{t("admin.import_qaLanguagesGenerated")}: <strong>{(r.languages_generated || []).length}</strong></span>
              {(r.warnings || []).length > 0 && (
                <span className="text-amber-700">{t("admin.import_qaWarnings")}: <strong>{(r.warnings || []).length}</strong></span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-slate-200">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg border-b-2 transition ${activeTab === tab.id ? "border-emerald-600 text-emerald-700 bg-emerald-50/40" : "border-transparent text-slate-600 hover:text-slate-900"}`}
            data-testid={`import-tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {t(`admin.import_${tab.key}`)}
          </button>
        ))}
        <div className="ms-auto">
          <button onClick={downloadTemplate} className="tsw-btn-ghost text-sm" data-testid="download-template">
            <Download className="w-4 h-4" /> {t("admin.import_downloadTemplate")}
          </button>
        </div>
      </div>

      {/* Tab content */}
      <div className="tsw-card p-5">
        {activeTab === "json_paste" && (
          <div className="space-y-3" data-testid="tab-json-paste">
            <label className="tsw-label">
              {t("admin.import_pasteJsonHere")}
            </label>
            <textarea
              className="tsw-input min-h-[260px] font-mono text-xs"
              value={rawJson}
              onChange={(e) => setRawJson(e.target.value)}
              placeholder={`{\n  "questionnaires": [\n    {\n      "title_en": "...",\n      "title_es": "...",\n      "profile_keys": ["player"],\n      "questions": []\n    }\n  ]\n}`}
              data-testid="import-paste-textarea"
            />
            <div className="flex justify-end">
              <button onClick={previewFromPaste} disabled={parsing} className="tsw-btn-secondary" data-testid="import-preview-paste">
                <Eye className="w-4 h-4" /> {parsing ? t("common.loading") : t("admin.import_preview")}
              </button>
            </div>
          </div>
        )}

        {(activeTab === "json_file" || activeTab === "docx_file") && (
          <div className="space-y-3" data-testid={`tab-${activeTab}`}>
            <label className="tsw-label">
              {activeTab === "json_file" ? t("admin.import_jsonFile") : t("admin.import_docxFile")}
            </label>
            <input
              ref={fileRef}
              type="file"
              accept={activeTab === "json_file" ? ".json,application/json" : ".docx"}
              onChange={onPickFile}
              className="block w-full text-sm text-slate-700 file:me-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100"
              data-testid={`import-file-${activeTab}`}
            />
            {activeTab === "docx_file" && (
              <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <div>
                  {t("admin.import_docxWarning")}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Preview & per-item editing */}
      {items.length > 0 && (
        <div className="space-y-4" data-testid="import-preview-panel">
          {warning && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
              <div>{warning}</div>
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="text-sm text-slate-600">
              {t("admin.import_source")}: <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">{source}</code>
              {" · "}
              {items.length} {t("admin.import_questionnairesReady")}
            </div>
            <button onClick={commit} disabled={committing || batchHasMissingOptions} className="tsw-btn-primary" data-testid="import-commit" title={batchHasMissingOptions ? t("admin.builderMissingOptionsInline") : undefined}>
              <CheckCircle2 className="w-4 h-4" />
              {committing ? t("common.saving") : t("admin.import_importAll")}
              <ArrowRight className="w-4 h-4 rtl:rotate-180" />
            </button>
          </div>

          {batchHasMissingOptions && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 flex items-start gap-2" data-testid="import-missing-options-batch">
              <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
              <div>{t("admin.builderMissingOptionsBatch")}</div>
            </div>
          )}

          <div className="space-y-4">
            {items.map((it, idx) => {
              const sCount = sensitiveCount(it);
              const qCount = (it.questions || []).length;
              const missingIdxs = missingOptionsIdxs(it);
              return (
                <div key={it._uid || `${it.title_en}-${idx}`} className="tsw-card p-5 space-y-3" data-testid={`import-item-${idx}`}>
                  {missingIdxs.length > 0 && (
                    <div className="rounded-lg border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-900 flex items-start gap-2" data-testid={`import-item-missing-options-${idx}`}>
                      <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                      <div>
                        {t("admin.builderMissingOptionsInline")}
                        <span className="font-mono ms-1">#{missingIdxs.join(", #")}</span>
                      </div>
                    </div>
                  )}
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-display font-semibold text-slate-900 text-lg leading-tight">{titleStr(it)}</div>
                      <div className="text-xs text-slate-500 mt-1">
                        {it.title_en && <>EN · {it.title_en} · </>}
                        {it.title_es && <>ES · {it.title_es} · </>}
                        {it.title_zh && <>ZH · {it.title_zh}</>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="tsw-chip bg-emerald-50 text-emerald-700">{qCount} {t("admin.import_questionsLabel")}</span>
                      {sCount > 0 && <span className="tsw-chip bg-amber-50 text-amber-800 border border-amber-200">{sCount} {t("admin.import_sensitiveLabel")}</span>}
                      {it.is_internal && <span className="tsw-chip bg-slate-100 text-slate-700">{t("admin.filterInternal").toLowerCase()}</span>}
                      {!it.is_active && <span className="tsw-chip bg-slate-100 text-slate-600">{t("admin.statusInactive").toLowerCase()}</span>}
                      <button onClick={() => removeItem(idx)} className="tsw-btn-ghost text-rose-600" title="Remove from this batch" data-testid={`import-item-remove-${idx}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className="tsw-label">{t("admin.import_assignToProfiles")}</label>
                      <div className="flex flex-wrap gap-1.5">
                        {profiles.map((p) => {
                          const checked = (it.profile_keys || []).includes(p.key);
                          return (
                            <label key={p.key} className={`tsw-chip cursor-pointer text-xs ${checked ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-700"}`}>
                              <input type="checkbox" className="sr-only" checked={checked} onChange={(e) => updateItemField(idx, {
                                profile_keys: e.target.checked ? [...(it.profile_keys || []), p.key] : (it.profile_keys || []).filter((k) => k !== p.key),
                              })} />
                              {tProfile(p.key, p)}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 sm:items-end">
                      <label className="text-sm flex items-center gap-2">
                        <input type="checkbox" checked={!!it.is_active} onChange={(e) => updateItemField(idx, { is_active: e.target.checked })} data-testid={`import-item-active-${idx}`} />
                        {t("admin.import_publishOnImport")}
                      </label>
                      <label className="text-sm flex items-center gap-2">
                        <input type="checkbox" checked={!!it.is_internal} onChange={(e) => updateItemField(idx, { is_internal: e.target.checked, profile_keys: e.target.checked ? [] : it.profile_keys })} data-testid={`import-item-internal-${idx}`} />
                        {t("admin.import_internalStaffOnly")}
                      </label>
                    </div>
                  </div>

                  <details className="rounded-lg border border-slate-100 bg-slate-50/50 p-3">
                    <summary className="text-sm font-medium text-slate-700 cursor-pointer flex items-center gap-2">
                      <Edit3 className="w-4 h-4" />
                      {t("admin.import_inspectQuestions")} ({qCount})
                    </summary>
                    <ol className="mt-3 space-y-2 text-sm">
                      {(it.questions || []).map((q, qi) => {
                        const tags = q.sensitive_tags || [];
                        return (
                          <li key={q.id || `${idx}-q-${qi}`} className="flex flex-col gap-0.5 border-b border-slate-100 pb-2 last:border-0">
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-slate-400 font-mono">#{qi + 1}</span>
                              <span className="tsw-chip bg-slate-100 text-slate-700 text-[10px]">{q.type}</span>
                              {q.required && <span className="tsw-chip bg-rose-50 text-rose-700 text-[10px]">required</span>}
                              {tags.map((tag) => <span key={tag} className="tsw-chip bg-amber-50 text-amber-700 text-[10px]">{tag}</span>)}
                            </div>
                            <div className="text-slate-900">{q.label_en}</div>
                            {q.label_es && q.label_es !== q.label_en && <div className="text-slate-500 text-xs">ES · {q.label_es}</div>}
                            {q.label_zh && <div className="text-slate-500 text-xs">ZH · {q.label_zh}</div>}
                            {(q.options || []).length > 0 && (
                              <ul className="mt-1 ms-6 list-disc text-xs text-slate-500">
                                {q.options.map((o, oi) => <li key={`${q.id || qi}-o-${oi}-${o.value}`}>{o.label_en}{o.label_es && o.label_es !== o.label_en ? ` · ${o.label_es}` : ""}{o.label_zh ? ` · ${o.label_zh}` : ""}</li>)}
                              </ul>
                            )}
                          </li>
                        );
                      })}
                    </ol>
                  </details>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* History */}
      <div className="tsw-card p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="font-semibold text-slate-900">
            {t("admin.import_importHistory")}
          </div>
          <Link to="/admin/questionnaires" className="text-sm text-emerald-700 font-medium" data-testid="link-questionnaires">
            {t("admin.import_goToQuestionnaires")} →
          </Link>
        </div>
        <ul className="space-y-2 text-sm">
          {history.length === 0 && <li className="text-slate-400">—</li>}
          {history.slice(0, 12).map((ev) => (
            <li key={ev.id} className="flex items-center justify-between border-b border-slate-100 pb-2 last:border-0">
              <span className="text-slate-700">{ev.title_en}</span>
              <span className="text-xs text-slate-500">{(ev.profile_keys || []).join(", ") || "—"} · {new Date(ev.created_at).toLocaleString()}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default AdminImport;
